import { AdPlatform } from "@repo/graphql/gql/graphql";
import { Platform } from "react-native";
import {
  conversionEventId,
  metaEventName,
  tiktokEventName,
  type AdConversionEvent,
} from "./conversions";
import { metaPixelSnippet, tiktokPixelSnippet } from "./snippets";

declare global {
  interface Window {
    /** Meta's pixel: a queueing stub until `fbevents.js` replaces it. */
    fbq?: (
      command: "track",
      event: string,
      params?: Record<string, never>,
      options?: { eventID: string },
    ) => void;
    /** TikTok's pixel: its methods queue until `events.js` arrives. */
    ttq?: {
      page: () => void;
      track: (
        event: string,
        params?: Record<string, never>,
        options?: { event_id: string },
      ) => void;
    };
  }
}

export type PixelConfig = { platform: AdPlatform; pixelId: string };

type PendingConversion = { event: AdConversionEvent; eventId: string };

const SNIPPETS: Record<AdPlatform, (pixelId: string) => string | null> = {
  [AdPlatform.Meta]: metaPixelSnippet,
  [AdPlatform.TikTok]: tiktokPixelSnippet,
};

const loaded = new Set<AdPlatform>();
const reported = new Set<string>();
// Conversions asked for before the pixels were loaded (the configs are fetched on
// the page): they go out as soon as a pixel is there.
let pending: PendingConversion[] = [];
// No pixel is going to load on this page: conversions are dropped, not queued.
let pixelsUnavailable = false;
// Callers holding a navigation back until `pending` is empty (`conversionsSettled`).
const settleWaiters = new Set<() => void>();

const hasBrowser = () =>
  Platform.OS === "web" && typeof document !== "undefined";

const report = ({ event, eventId }: PendingConversion) => {
  if (loaded.has(AdPlatform.Meta)) {
    window.fbq?.("track", metaEventName(event), {}, { eventID: eventId });
  }
  if (loaded.has(AdPlatform.TikTok)) {
    window.ttq?.track(tiktokEventName(event), {}, { event_id: eventId });
  }
};

const settleConversions = (): void => {
  [...settleWaiters].forEach((resolve) => resolve());
};

/**
 * Gives up on the conversions waiting for a pixel: none is going to load (nothing
 * is configured, or the configs could not be read).
 */
export const abandonPendingConversions = (): void => {
  pixelsUnavailable = true;
  pending = [];
  settleConversions();
};

/**
 * Loads the official base code of each configured platform, once per page load —
 * a platform that is already there, or has no (valid) id, is skipped. Web only.
 */
export const loadAdPixels = (configs: PixelConfig[]): void => {
  if (!hasBrowser()) return;
  for (const { platform, pixelId } of configs) {
    if (loaded.has(platform)) continue;
    const code = SNIPPETS[platform](pixelId);
    if (!code) continue;
    const script = document.createElement("script");
    script.text = code;
    document.head.appendChild(script);
    loaded.add(platform);
  }
  if (loaded.size === 0) {
    abandonPendingConversions();
    return;
  }
  pixelsUnavailable = false;
  const queued = pending;
  pending = [];
  queued.forEach(report);
  settleConversions();
};

/**
 * Resolves once no conversion is waiting for a pixel — they went out, or were
 * abandoned — or after `timeoutMs`, whichever comes first. Await it before a
 * navigation that would unmount the pixel loader, so a conversion asked for while
 * the configs were still being fetched is not lost; the timeout keeps a pixel that
 * never loads from holding the navigation.
 */
export const conversionsSettled = (timeoutMs: number): Promise<void> => {
  if (pending.length === 0) return Promise.resolve();
  return new Promise((resolve) => {
    const settle = () => {
      clearTimeout(timer);
      settleWaiters.delete(settle);
      resolve();
    };
    const timer = setTimeout(settle, timeoutMs);
    settleWaiters.add(settle);
  });
};

/** A page view on every loaded pixel. */
export const trackPageView = (): void => {
  if (!hasBrowser()) return;
  if (loaded.has(AdPlatform.Meta)) window.fbq?.("track", "PageView");
  if (loaded.has(AdPlatform.TikTok)) window.ttq?.page();
};

/**
 * Reports a funnel conversion to the loaded pixels with the id the backend's
 * server-side copy carries, so the platforms count it once. Each id goes out once.
 */
export const trackConversion = (
  event: AdConversionEvent,
  brandId: string,
): void => {
  if (!hasBrowser()) return;
  const eventId = conversionEventId(event, brandId);
  if (reported.has(eventId)) return;
  reported.add(eventId);
  if (loaded.size === 0) {
    if (!pixelsUnavailable) pending.push({ event, eventId });
    return;
  }
  report({ event, eventId });
};
